import React, { useState } from "react";
import { Button, Form, Alert, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { HiOutlineTrash, HiOutlinePlus } from "react-icons/hi";
import { ModalComponent } from "../../../../Components/Modal";

const EMPTY_FORM = {
  name: "",
  first_name: "",
  last_name: "",
  address_1: "",
  address_2: "",
  city: "",
  postcode: "",
  phone: "",
};

function validateForm(form, t) {
  const errors = {};
  if (!form.name.trim()) errors.name = t("Obligatorio");
  if (!form.first_name.trim()) errors.first_name = t("Obligatorio");
  if (!form.last_name.trim()) errors.last_name = t("Obligatorio");
  if (!form.address_1.trim()) errors.address_1 = t("Obligatorio");
  if (!form.city.trim()) errors.city = t("Obligatorio");
  if (!form.postcode.trim() || !/^(0[1-9]|[1-4][0-9]|5[0-2])\d{3}$/.test(form.postcode)) {
    errors.postcode = t("CP no válido (01-52XXX)");
  }
  if (!form.phone.trim() || form.phone.trim().length < 9) {
    errors.phone = t("Teléfono no válido");
  }
  return errors;
}

export const ShippingAddressModal = ({
  show,
  setShow,
  addresses,
  loading,
  error,
  onAdd,
  onDelete,
  selectedAddress,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSave = async () => {
    const errors = validateForm(form, t);
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    setSaving(true);
    setSaveError(null);
    const result = await onAdd(form);
    setSaving(false);
    if (result) {
      setShowForm(false);
      setForm(EMPTY_FORM);
      setFormErrors({});
    } else {
      setSaveError(t("Error al guardar la dirección"));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setSaveError(null);
  };

  const handleClose = () => {
    setShow(false);
    setShowForm(false);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setSaveError(null);
  };

  return (
    <ModalComponent show={show} setShow={setShow} title={showForm ? t("Nueva dirección") : t("Tus direcciones")}>
      {showForm ? (
        <div>
          {saveError && <Alert variant="danger" className="small py-2">{saveError}</Alert>}
          <Form>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">{t("Nombre de la dirección")}</Form.Label>
              <Form.Control
                name="name"
                value={form.name}
                onChange={handleInputChange}
                isInvalid={!!formErrors.name}
                placeholder={t("Ej: Casa, Trabajo...")}
                size="sm"
              />
              {formErrors.name && <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>}
            </Form.Group>

            <div className="row g-2 mb-2">
              <div className="col">
                <Form.Label className="small mb-1">{t("Nombre")}</Form.Label>
                <Form.Control
                  name="first_name"
                  value={form.first_name}
                  onChange={handleInputChange}
                  isInvalid={!!formErrors.first_name}
                  size="sm"
                />
                {formErrors.first_name && <Form.Control.Feedback type="invalid">{formErrors.first_name}</Form.Control.Feedback>}
              </div>
              <div className="col">
                <Form.Label className="small mb-1">{t("Apellidos")}</Form.Label>
                <Form.Control
                  name="last_name"
                  value={form.last_name}
                  onChange={handleInputChange}
                  isInvalid={!!formErrors.last_name}
                  size="sm"
                />
                {formErrors.last_name && <Form.Control.Feedback type="invalid">{formErrors.last_name}</Form.Control.Feedback>}
              </div>
            </div>

            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">{t("Dirección")}</Form.Label>
              <Form.Control
                name="address_1"
                value={form.address_1}
                onChange={handleInputChange}
                isInvalid={!!formErrors.address_1}
                size="sm"
              />
              {formErrors.address_1 && <Form.Control.Feedback type="invalid">{formErrors.address_1}</Form.Control.Feedback>}
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">{t("Piso / Puerta (opcional)")}</Form.Label>
              <Form.Control
                name="address_2"
                value={form.address_2}
                onChange={handleInputChange}
                size="sm"
              />
            </Form.Group>

            <div className="row g-2 mb-2">
              <div className="col-8">
                <Form.Label className="small mb-1">{t("Ciudad")}</Form.Label>
                <Form.Control
                  name="city"
                  value={form.city}
                  onChange={handleInputChange}
                  isInvalid={!!formErrors.city}
                  size="sm"
                />
                {formErrors.city && <Form.Control.Feedback type="invalid">{formErrors.city}</Form.Control.Feedback>}
              </div>
              <div className="col-4">
                <Form.Label className="small mb-1">{t("C.P.")}</Form.Label>
                <Form.Control
                  name="postcode"
                  value={form.postcode}
                  onChange={handleInputChange}
                  isInvalid={!!formErrors.postcode}
                  size="sm"
                  maxLength={5}
                  placeholder="08001"
                />
                {formErrors.postcode && <Form.Control.Feedback type="invalid">{formErrors.postcode}</Form.Control.Feedback>}
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="small mb-1">{t("Teléfono")}</Form.Label>
              <Form.Control
                name="phone"
                value={form.phone}
                onChange={handleInputChange}
                isInvalid={!!formErrors.phone}
                size="sm"
                placeholder="+34 600 123 456"
              />
              {formErrors.phone && <Form.Control.Feedback type="invalid">{formErrors.phone}</Form.Control.Feedback>}
            </Form.Group>

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                className="w-50 rounded-pill"
                size="sm"
                onClick={handleCancel}
              >
                {t("Cancelar")}
              </Button>
              <Button
                variant="dark"
                className="w-50 rounded-pill"
                size="sm"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <Spinner animation="border" size="sm" /> : t("Guardar")}
              </Button>
            </div>
          </Form>
        </div>
      ) : (
        <div>
          {error && <Alert variant="danger" className="small py-2">{error}</Alert>}

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : addresses.length === 0 ? (
            <p className="text-muted small text-center py-3 mb-2">
              {t("No tienes direcciones guardadas")}
            </p>
          ) : (
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              {addresses.map((addr) => {
                const isSelected = selectedAddress?.id === addr.id;
                return (
                  <div
                    key={addr.id}
                    role="button"
                    className={`card border p-3 mb-2 ${isSelected ? "border-success bg-light" : ""}`}
                    onClick={() => onSelect(addr)}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          {isSelected && (
                            <span className="badge bg-success rounded-pill">✓</span>
                          )}
                          <p className="fw-bold mb-0 small">{addr.name}</p>
                        </div>
                        <p className="small text-muted mb-0">
                          {addr.first_name} {addr.last_name}
                        </p>
                        <p className="small text-muted mb-0">
                          {addr.address_1}
                          {addr.address_2 ? `, ${addr.address_2}` : ""}
                        </p>
                        <p className="small text-muted mb-0">
                          {addr.postcode} {addr.city}
                        </p>
                      </div>
                      <Button
                        variant="link"
                        className="text-danger p-0"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(t("¿Eliminar esta dirección?"))) {
                            onDelete(addr.id);
                          }
                        }}
                      >
                        <HiOutlineTrash size={16} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="d-grid mt-2">
            <Button
              variant="outline-dark"
              size="sm"
              className="rounded-pill d-flex align-items-center justify-content-center gap-1"
              onClick={() => setShowForm(true)}
            >
              <HiOutlinePlus size={16} />
              {t("Añadir nueva dirección")}
            </Button>
          </div>
        </div>
      )}
    </ModalComponent>
  );
};
